import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import moment from "moment";
import { CardHelper } from "./card-helper";
import { CardBrand, CardNumberLength, CCVLength } from "./enums";

export class CustomValidators {
  static validateCreditCard(control: AbstractControl): ValidationErrors | null {
    const cardNumber: string = control.value?.trim() || "";

    if (!cardNumber) {
      return null;
    }

    const sanitizedCardNumber = cardNumber.replace(/\D/g, "");

    if (
      sanitizedCardNumber.length < CardNumberLength.MIN ||
      sanitizedCardNumber.length > CardNumberLength.MAX
    ) {
      return { cardLength: true };
    }

    if (!CardHelper.luhnCheck(sanitizedCardNumber)) {
      return { invalidCard: true };
    }

    const cardType = CardHelper.getTypeByCardNumber(sanitizedCardNumber);
    if (!cardType) {
      return { invalidCardType: true };
    }

    return null;
  }

  static validateCCV(formControlName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const ccv: string = control.value?.trim() || "";
      const cardNumber: string =
        control.parent?.get(formControlName)?.value?.trim() || "";
      const cardType = CardHelper.getTypeByCardNumber(cardNumber);

      if (cardType === CardBrand.AMEX && ccv.length !== CCVLength.AMEX) {
        return { ccvLength: true, cardType };
      } else if (cardType !== CardBrand.AMEX && ccv.length === CCVLength.AMEX) {
        return { ccvLength: true, cardType };
      }

      return null;
    };
  }

  static validateName(control: AbstractControl): ValidationErrors | null {
    const name: string = control.value?.trim() || "";

    if (!name) {
      return null;
    }

    if (name.length < 2) {
      return { nameTooShort: true };
    }

    if (name.length > 100) {
      return { nameTooLong: true };
    }

    if (name.trim().length === 0) {
      return { nameEmpty: true };
    }

    if (/\d/.test(name)) {
      return { numbersNotAllowed: true };
    }

    if (/[._#$%&*+=|\\<>?/]/.test(name)) {
      return { specialCharsNotAllowed: true };
    }

    const nameRegex = /^[a-zA-ZÀ-ÿ\u00C0-\u017F\s\-']+$/;

    if (!nameRegex.test(name)) {
      return { invalidName: true };
    }

    const nameParts = name.split(/\s+/).filter(part => part.trim().length > 0);
    if (nameParts.length < 2) {
      return { nameAndSurnameRequired: true };
    }

    return null;
  }

  static minAgeYears(minAge: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const raw = control.value;
      if (raw == null || String(raw).trim().length < 10) {
        return null;
      }
      const birth = moment(String(raw).trim(), "DD/MM/YYYY", true);
      if (!birth.isValid()) {
        return null;
      }
      const ageYears = moment().diff(birth, "years");
      if (ageYears < minAge) {
        return { underage: { minAge } };
      }
      return null;
    };
  }
}
